import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '../../../base/outline-element/outline-element';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';
import componentStyles from './sic-nav-item.css.lit';
import { debounce } from '../../../../utilities/debounce';
import {
  screenSizeMap,
  ScreenSizeController,
} from '../../../controllers/screen-size-controller';
import { getCurrentPath, _dispatchActiveEvent } from '../utility';

import { MenuNavItem } from '../types';

/**
 * The SicNavItem component
 * @element sic-nav-item
 * @slot sub - for sic-nav-item/sic-nav-link
 */
@customElement('sic-nav-item')
export class SicNavItem extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  private _seed = Math.floor(Math.random() * 100000).toString();
  screens = new ScreenSizeController(this);

  /**
   * Item data
   */
  @property({ type: Object, attribute: 'item' })
  item: MenuNavItem;

  /**
   * Attribute used to determine if the item is a top level parent item.
   */
  @property({ type: Boolean })
  toplevel: boolean;

  /**
   * ID of parent element.
   */
  @property({ type: String, reflect: false, attribute: 'parent-id' })
  parentID: string;

  /**
   * Id used for accessibility purposes.
   */
  @property({ type: String, attribute: 'wrapper-id' })
  wrapperID = `list-item-${this._seed}`;

  /**
   * Open state of menu.
   */
  @property({ type: Boolean, attribute: 'open', reflect: true })
  open = false;

  @property({ type: Boolean })
  isMobile: boolean = window.innerWidth < (screenSizeMap.get('lg') as number);

  render(): TemplateResult {
    const wrapperClasses = {
      'list-item': !this.toplevel,
      'list-item--main': this.toplevel,
      'mobile': this.isMobile,
    };

    const listClasses = {
      'list-item--sub-menu': true,
      'columns': this.item.sub && this.item.sub.length > 7 ? true : false,
      'mobile': this.isMobile,
    };

    return html`
      <div
        class="${classMap(wrapperClasses)}"
        id="${this.wrapperID}"
        @activeListener=${this.updateActive}
        ?engaged=${this.open}
      >
        ${this.isMobile ? this.buttonTemplate() : this.linkTemplate()}
        <ul
          class="${classMap(listClasses)}"
          aria-label="${this.item.text} sub menu"
          id="${this._seed}-sub-menu"
        >
          ${this.isMobile ? this.specialLinkTemplate() : null}
          ${this.generateListItems()}
        </ul>
      </div>
    `;
  }

  firstUpdated() {
    window.addEventListener(
      'resize',
      debounce(() => this.handleResize(), 100)
    );
    this.checkActive();
  }
  disconnectedCallback() {
    window.removeEventListener(
      'resize',
      debounce(() => this.handleResize(), 100)
    );
  }

  /**
   * Updates the active status if activeListener event returns item.id.
   * @param e
   */
  updateActive(e: CustomEvent) {
    const childElementActive = e.detail.parentID === this.item.id;
    if (childElementActive) {
      this.setAttribute('active', 'active');
      _dispatchActiveEvent(this);
    }
  }

  /**
   * Checks if this.item.id matches current window path, emits active event if true.
   */
  checkActive() {
    if (this.item.url && getCurrentPath(this.item.url)) {
      this.setAttribute('active', 'active');
      if (this.item.parentID) {
        _dispatchActiveEvent(this);
      }
    }
  }

  /**
   * Processes this.item.sub into menu sub-components as needed.
   * @returns Menu sub-components
   */
  generateListItems() {
    if (this.item && this.item.sub) {
      return this.item.sub.map((listItem: MenuNavItem) => {
        const hasSubList = Object?.keys(listItem)?.includes('sub')
          ? true
          : false;
        if (hasSubList) {
          return html` <li>
            <sic-nav-item
              .item=${listItem}
              id=${listItem.id}
              parent-id=${this.id}
            ></sic-nav-item>
          </li>`;
        } else {
          return html`<li>
            <sic-nav-link
              id=${listItem.id}
              .item=${listItem}
              parent-id=${this.id}
            ></sic-nav-link>
          </li>`;
        }
      });
    }
    return;
  }

  /**
   * Generates mobile sub-menu top item.
   */
  specialLinkTemplate() {
    return html`
      <li class="list-item--special-link">
        <a aria-label=${ifDefined(this.item.aria)} href="${this.item.url}"
          >${this.item.text}</a
        >
      </li>
    `;
  }

  /**
   * Returns the menu link
   */
  linkTemplate() {
    return html`
      <a
        class="list-item--link"
        aria-label=${ifDefined(this.item.aria)}
        href=${ifDefined(this.item.url)}
      >
        ${this.item.text}
      </a>
    `;
  }

  /**
   * Returns required span template.
   */
  spanTemplate() {
    if (this.open && this.isMobile) {
      if (this.toplevel) {
        this.parentElement?.classList.add('back');
        return html`
          <span class="list-item--label-return">Back to Main Menu</span>
        `;
      } else {
        const header = document.querySelector('sic-main-header') as HTMLElement;
        const parent = header
          .querySelector('sic-nav')
          ?.shadowRoot?.getElementById(this.parentID) as SicNavItem;
        const label = parent ? `to ${parent.item.text}` : '';

        return html`
          <span class="list-item--label-return">Back ${label}</span>
        `;
      }
    } else {
      return html` <span class="list-item--label"> ${this.item.text} </span> `;
    }
  }

  /**
   * Return required icon template.
   */
  iconTemplate() {
    if (this.isMobile) {
      if (this.open) {
        return html`
          <sic-icon
            size="14px"
            class="list-item--button-icon-mobile"
            name="chevron-left"
            svg-fill="var(--color-primary-default)"
            aria-hidden="true"
          ></sic-icon>
        `;
      } else {
        return html`
          <sic-icon
            size="14px"
            class="list-item--button-icon-mobile"
            name="${this.open ? 'chevron-down' : 'chevron-right'}"
            svg-fill="var(--color-primary-default)"
            aria-hidden="true"
          ></sic-icon>
        `;
      }
    } else {
      return html` <sic-icon
        size="22px"
        class="list-item--button-icon"
        name="${this.open ? 'chevron-up' : 'chevron-down'}"
        svg-fill="var(--color-primary-default)"
        aria-hidden="true"
      ></sic-icon>`;
    }
  }

  /**
   * Swaps the order of the button content depending open and mobile states.
   */
  getButtonContent() {
    return html`
      ${this.isMobile && this.open ? this.iconTemplate() : this.spanTemplate()}
      ${this.isMobile && this.open ? this.spanTemplate() : this.iconTemplate()}
    `;
  }

  buttonTemplate() {
    return html`
      <button
        class="list-item--button ${this.isMobile ? 'mobile' : null}"
        id="${this._seed}-button"
        aria-controls="${this._seed}-sub-menu"
        aria-label="${this.item.aria
          ? this.item.aria
          : `expand ${this.item.text}`} "
        aria-expanded="${this.open}"
        aria-haspopup="true"
        aria-labelledby=${this.item.url
          ? '${this.seed}-link'
          : '${this.seed}-label'}
        @click=${this.handleToggleMenu}
        @keydown=${this.handleToggleMenu}
      >
        ${this.getButtonContent()}
      </button>
    `;
  }

  /**
   * detects if component has switched between desktop and mobile sizes.
   */
  handleResize() {
    const hasSwitched =
      this.isMobile !== window.innerWidth < (screenSizeMap.get('lg') as number);
    if (hasSwitched) {
      this.isMobile = window.innerWidth < (screenSizeMap.get('lg') as number);
      this.open = false;
      this.removeMobileFlyout();
    }
  }

  /**
   * Finds all sibling elements.
   * @returns Array<HTMLElement>
   */
  getSiblingNavItems() {
    const parent = this.parentElement?.parentElement;
    let array: Array<Element> = [];
    if (parent) {
      array = [
        ...parent?.querySelectorAll('sic-nav-link, sic-nav-item'),
      ].filter(el => el.id !== this.item.id);
    }
    return array;
  }

  /**
   * Adds the 'flyaway' class to all elements returned by this.getSiblingNavItems.
   */
  applyMobileFlyout() {
    this.getSiblingNavItems().map(el =>
      el.parentElement!.classList.add('flyaway')
    );
    if (!this.toplevel) {
      const list = this.shadowRoot?.getElementById(
        `${this._seed}-sub-menu`
      ) as HTMLElement;
      const main = document.querySelector('sic-main-header') as HTMLElement;
      setTimeout(
        () =>
          main.style.setProperty(
            '--mobile-utility-offset',
            `${list.offsetHeight - 54}px`
          ),
        0
      );
    }
  }

  /**
   * Removes the 'flyaway' class to all elements returned by this.getSiblingNavItems.
   */
  removeMobileFlyout() {
    this.getSiblingNavItems().map(el =>
      el.parentElement!.classList.remove('flyaway')
    );

    if (!this.toplevel) {
      const main = document.querySelector('sic-main-header') as HTMLElement;
      setTimeout(() => main.style.removeProperty('--mobile-utility-offset'), 0);
    }
  }

  removeMobileBack() {
    this.parentElement?.classList.contains('back')
      ? this.parentElement.classList.remove('back')
      : null;
  }

  /**
   * Clears flyaway class and open attribute on child elements.
   */
  clearChildStatus() {
    const openChildren = this.shadowRoot?.querySelectorAll('[open]');
    const flyawayChildren = this.shadowRoot?.querySelectorAll('.flyaway');
    if (openChildren?.length) {
      openChildren.forEach(child => {
        const openEl = child as SicNavItem;
        openEl.open = false;
        openEl.clearChildStatus();
      });
    }
    if (flyawayChildren?.length) {
      flyawayChildren.forEach(child => {
        child.classList.remove('flyaway');
      });
    }
  }

  /**
   * Toggles flyout class based on this.open status.
   */
  toggleMobileFlyout() {
    if (this.open) {
      this.removeMobileFlyout();
      this.removeMobileBack();
    } else {
      this.applyMobileFlyout();
    }
  }

  /**
   * Handles flyout and open status.
   */
  toggleMenu() {
    if (this.isMobile) {
      this.toggleMobileFlyout();
    }
    if (this.open) {
      this.open = !this.open;
    } else {
      this.open = !this.open;
    }
  }

  /**
   * Toggles menu on space and Enter keyboard events.
   */
  handleToggleMenu(e: KeyboardEvent) {
    const keyed = e.type === 'keydown';
    if (keyed) {
      // make sure menu toggles only on Space and Enter keys
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggleMenu();
      }
    } else {
      this.toggleMenu();
    }
  }
}
